import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, FileDown, Globe, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTORS = [
  {
    value: "charity_no_yp",
    label: "Charity / Non-profit",
    detail: "Not working with young people / not in education.",
  },
  {
    value: "charity_yp",
    label: "Charity working with young people",
    detail: "Includes safeguarding framework.",
  },
  {
    value: "school_trust",
    label: "School / Academy / Multi-Academy Trust",
    detail: "DfE statutory requirements + safeguarding + Charity Commission.",
  },
  {
    value: "other",
    label: "Other organisation",
    detail: "Just SEO, security and GDPR.",
  },
];

const inputCls =
  "bg-[rgba(14,11,13,0.55)] border-[rgba(244,236,224,0.18)] text-[#F4ECE0] placeholder:text-[rgba(244,236,224,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(244,236,224,0.55)] focus-visible:ring-offset-0";

export default function AuditDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1); // 1: url+sector, 2: running, 3: done/error
  const [url, setUrl] = useState("");
  const [sector, setSector] = useState("charity_yp");
  const [depth, setDepth] = useState("quick"); // quick | deep
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [downloadedName, setDownloadedName] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset shortly after close so the closing animation isn't jumpy
      const t = setTimeout(() => {
        setStep(1);
        setUrl("");
        setSector("charity_yp");
        setDepth("quick");
        setRunning(false);
        setError("");
        setDownloadedName("");
      }, 350);
      return () => clearTimeout(t);
    }
  }, [open]);

  const looksLikeUrl = useMemo(() => /\.[a-z]{2,}/i.test(url.trim()), [url]);

  const start = async (e) => {
    if (e) e.preventDefault();
    if (!looksLikeUrl || running) return;
    setError("");
    setRunning(true);
    setStep(2);
    try {
      const sectorMeta = SECTORS.find((s) => s.value === sector) || SECTORS[0];
      const works_with_young_people =
        sector === "charity_yp" || sector === "school_trust";
      const resp = await axios.post(
        `${API}/audit`,
        {
          url: url.trim(),
          sector,
          works_with_young_people,
          depth,
        },
        { responseType: "blob", timeout: 180000 }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      // Extract filename from Content-Disposition
      let filename = "AnthonyMagee_Audit.pdf";
      const cd = resp.headers["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }
      // Trigger download
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      setDownloadedName(filename);
      toast.success(`Audit ready (${sectorMeta.label}). PDF downloaded.`);
      setStep(3);
    } catch (err) {
      let detail =
        "Something went wrong running the audit. Try again, or pick a different URL.";
      if (err?.response?.data instanceof Blob) {
        // Attempt to parse blob as JSON error
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.detail) detail = parsed.detail;
        } catch {
          // ignore
        }
      } else if (err?.response?.data?.detail) {
        detail = err.response.data.detail;
      } else if (err?.message) {
        detail = err.message;
      }
      setError(detail);
      setStep(3);
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E0B0D] border border-[rgba(244,236,224,0.18)] text-[#F4ECE0] sm:max-w-[520px] rounded-3xl"
        data-testid="audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#F4ECE0] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#FF4FA3]" />
            Audit my website
          </DialogTitle>
          <DialogDescription className="text-[rgba(244,236,224,0.72)]">
            {step === 1 &&
              "I'll check SEO, security, GDPR — and the right compliance framework for your sector. Free, you get a branded PDF."}
            {step === 2 && (depth === "deep"
              ? "Crawling your sitemap, fetching evidence pages (safeguarding, accounts, privacy, etc), then writing it up in my voice…"
              : "Scanning your homepage, calling the regulator playbook, writing it up in my voice…")}
            {step === 3 && !error && "Done. Your PDF should be in your downloads folder."}
            {step === 3 && error && "Couldn't finish the audit."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={start} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label
                htmlFor="audit-url"
                className="text-[rgba(244,236,224,0.85)] text-sm flex items-center gap-1.5"
              >
                <Globe className="h-3.5 w-3.5" />
                Your website URL
              </Label>
              <Input
                id="audit-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-charity.org"
                autoFocus
                className={inputCls}
                data-testid="audit-url-input"
              />
              <p className="text-[11px] text-[rgba(244,236,224,0.55)]">
                I'll only fetch the homepage — nothing destructive, no login required.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-[rgba(244,236,224,0.85)] text-sm">
                What kind of organisation is it?
              </Label>
              <div className="grid gap-2">
                {SECTORS.map((s) => (
                  <label
                    key={s.value}
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                      sector === s.value
                        ? "border-[#FF4FA3] bg-[rgba(255,79,163,0.08)]"
                        : "border-[rgba(244,236,224,0.16)] bg-[rgba(244,236,224,0.04)] hover:border-[rgba(244,236,224,0.32)]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="audit-sector"
                      value={s.value}
                      checked={sector === s.value}
                      onChange={() => setSector(s.value)}
                      className="mt-0.5 h-4 w-4 accent-[#FF4FA3]"
                      data-testid={`audit-sector-${s.value}`}
                    />
                    <span className="leading-snug">
                      <span className="block text-sm font-semibold text-[#F4ECE0]">
                        {s.label}
                      </span>
                      <span className="block text-xs text-[rgba(244,236,224,0.65)] mt-0.5">
                        {s.detail}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[rgba(244,236,224,0.85)] text-sm">
                How deep should I go?
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <label
                  className={`flex flex-col gap-1 rounded-xl border p-3 cursor-pointer transition-colors ${
                    depth === "quick"
                      ? "border-[#FF4FA3] bg-[rgba(255,79,163,0.08)]"
                      : "border-[rgba(244,236,224,0.16)] bg-[rgba(244,236,224,0.04)] hover:border-[rgba(244,236,224,0.32)]"
                  }`}
                >
                  <span className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#F4ECE0]">
                      Quick
                    </span>
                    <input
                      type="radio"
                      name="audit-depth"
                      value="quick"
                      checked={depth === "quick"}
                      onChange={() => setDepth("quick")}
                      className="h-4 w-4 accent-[#FF4FA3]"
                      data-testid="audit-depth-quick"
                    />
                  </span>
                  <span className="text-xs text-[rgba(244,236,224,0.65)] leading-snug">
                    Homepage only · ~20-30s · smoke-test verdict.
                  </span>
                </label>
                <label
                  className={`flex flex-col gap-1 rounded-xl border p-3 cursor-pointer transition-colors ${
                    depth === "deep"
                      ? "border-[#FF4FA3] bg-[rgba(255,79,163,0.08)]"
                      : "border-[rgba(244,236,224,0.16)] bg-[rgba(244,236,224,0.04)] hover:border-[rgba(244,236,224,0.32)]"
                  }`}
                >
                  <span className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#F4ECE0]">
                      Deep
                    </span>
                    <input
                      type="radio"
                      name="audit-depth"
                      value="deep"
                      checked={depth === "deep"}
                      onChange={() => setDepth("deep")}
                      className="h-4 w-4 accent-[#FF4FA3]"
                      data-testid="audit-depth-deep"
                    />
                  </span>
                  <span className="text-xs text-[rgba(244,236,224,0.65)] leading-snug">
                    Crawls sitemap + key policy pages · ~60-90s · receipt-grade evidence.
                  </span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={running}
                className="text-[rgba(244,236,224,0.85)] hover:text-[#F4ECE0] hover:bg-[rgba(244,236,224,0.08)]"
                data-testid="audit-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!looksLikeUrl || running}
                className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7] disabled:opacity-50"
                data-testid="audit-submit-button"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Run audit
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="py-8 flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-[#FF4FA3]" />
            <div className="space-y-1">
              <p className="text-sm text-[#F4ECE0] font-semibold">
                Auditing {url}
              </p>
              <p className="text-xs text-[rgba(244,236,224,0.65)] max-w-sm">
                {depth === "deep"
                  ? "Deep audit running — fetching sitemap, crawling up to 10 policy/evidence pages, then writing the verdict in my voice. Usually 60-90 seconds."
                  : "Quick audit running — homepage scan + SEO/security/GDPR checks, then writing the verdict in my voice. Usually 20-30 seconds."}
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 pt-2">
            {error ? (
              <>
                <div className="rounded-xl border border-[rgba(255,90,122,0.4)] bg-[rgba(255,90,122,0.08)] p-3 text-sm text-[#F4ECE0]">
                  {error}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    className="text-[rgba(244,236,224,0.85)] hover:text-[#F4ECE0]"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => setStep(1)}
                    className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7]"
                    data-testid="audit-retry-button"
                  >
                    Try again
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border border-[rgba(244,236,224,0.18)] bg-[rgba(244,236,224,0.06)] p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#F4ECE0]">
                    <FileDown className="h-4 w-4 text-[#FF4FA3]" />
                    {downloadedName || "AnthonyMagee_Audit.pdf"}
                  </div>
                  <p className="text-xs text-[rgba(244,236,224,0.65)] mt-1">
                    Check your downloads folder. The PDF includes Anthony's
                    verdict, a scorecard, and recommendations against the right
                    UK frameworks.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => setStep(1)}
                    variant="ghost"
                    className="text-[rgba(244,236,224,0.85)] hover:text-[#F4ECE0]"
                    data-testid="audit-another-button"
                  >
                    Audit another site
                  </Button>
                  <Button
                    onClick={() => onOpenChange(false)}
                    className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7]"
                    data-testid="audit-close-button"
                  >
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
